import { useErrorReporter } from '@/composables/useErrorReporter'
import { useAddressLocalStorage } from '@/composables/useLocalStorage'
import {
	API_BASE,
	API_ENDPOINT_ADDRESS,
	API_ENDPOINT_ADDRESS_COORDINATES,
	API_ENDPOINT_ADDRESS_PLACE_ID
} from '@/constants/index.js'
import { isAddressCheckResponseValid } from '@/helpers/api-response-validators'
import type {
	AddressCoordinates,
	AddressResponse
} from '@/types/components/address-checker'
import { mapPackages } from '@/utils/mapPackages'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const loading = ref(false)
const billables = ref()

export function useProspectAddress() {
	const { setAddressObject } = useAddressLocalStorage()
	const { t } = useI18n({
		useScope: 'global'
	})
	const router = useRouter()
	const localePath = useLocalePath()

	const processAddressResponse = (data: any, showToastOnError = false) => {
		const { bugsnagReport } = useErrorReporter()

		billables.value = mapPackages(data?.billables)

		if (!isAddressCheckResponseValid(data)) return

		const {
			addressId = 0,
			zoneType = 'remote',
			address,
			accountFound,
			planPhaseLabel
		} = data as AddressResponse

		const { status } = setAddressObject(address, addressId, zoneType, billables.value, accountFound, planPhaseLabel)

		if (!status?.length) {
			bugsnagReport({
				error: new Error('Zone to Status mapping failed: fiber status is undefined'),
				showToast: showToastOnError,
				toast: {
					title: t('find-address.error'),
					message: t('find-address.no-address')
				}
			})
			return
		}

		if (accountFound) {
			router.push({
				path: localePath('/already-registered')
			})
		} else {
			router.push({
				path: localePath('/get-fiber'),
				query: {
					fiber: status
				}
			})
		}
	}

	const redirectToFindAddress = (zip?: string, address?: string) => {
		if (zip && address) {
			router.push({
				path: localePath('/find-address'),
				query: {
					zip,
					address
				}
			})
		}
	}

	async function getProspectAddressData(searchTerm: string, zip: string, campaignId?: string) {
		const { bugsnagReport } = useErrorReporter()

		let body: {
			address: string
			campaignId?: string
		} = {
			address: `${searchTerm}, ${zip}`
		}
		if (campaignId) {
			body = Object.assign(body, { campaignId })
		}

		loading.value = true

		return await $fetch(`${API_BASE}${API_ENDPOINT_ADDRESS}`, {
			method: 'POST',
			body,
			onResponse({ response }) {
				if (!response.ok) {
					redirectToFindAddress(zip, searchTerm)
					const errorMessage = response?._data?.error || response?._data?.errors[0]?.title
					throw new Error(errorMessage)
				}

				processAddressResponse(response._data)
			}
		}).catch(e => {
			const errorMessage = `${e} - Address: ${body.address}`
			bugsnagReport({
				error: new Error(errorMessage),
				showToast: false
			})
			return e
		}).finally(() => {
			loading.value = false
		})
	}

	async function getProspectAddressWithCoordinates(data: AddressCoordinates) {
		const { bugsnagReport } = useErrorReporter()

		const body = {
			lat: data.lat,
			lon: data.lng
		}

		loading.value = true

		return await $fetch(`${API_BASE}${API_ENDPOINT_ADDRESS_COORDINATES}`, {
			method: 'POST',
			body,
			onResponse({ response }) {
				if (!response.ok) {
					const errorMessage = response?._data?.error || response?._data?.errors[0]?.title
					throw new Error(errorMessage)
				}

				processAddressResponse(response._data, true)
			}
		}).catch(e => {
			const errorMessage = `${e} - Coordinates: ${body.lat}, ${body.lon}`
			bugsnagReport({
				error: new Error(errorMessage),
				showToast: true,
				toast: {
					title: t('find-address.error'),
					message: t('find-address.no-address')
				}
			})
			return e
		}).finally(() => {
			loading.value = false
		})
	}

	async function getProspectAddressWithPlaceId(placeId: string, zip: string, searchTerm: string, campaignId?: string) {
		const { bugsnagReport } = useErrorReporter()

		loading.value = true

		let body: {
			placeId: string
			campaignId?: string
		} = {
			placeId
		}

		if (campaignId) {
			body = Object.assign(body, { campaignId })
		}

		return await $fetch(`${API_BASE}${API_ENDPOINT_ADDRESS_PLACE_ID}`, {
			method: 'POST',
			body,
			onResponse({ response }) {
				if (!response.ok) {
					redirectToFindAddress(zip, searchTerm)
					const errorMessage = response?._data?.error || response?._data?.errors[0]?.title
					throw new Error(errorMessage)
				}

				processAddressResponse(response._data, true)
			}
		}).catch(e => {
			const errorMessage = `${e} - Place ID: ${placeId}`
			bugsnagReport({
				error: new Error(errorMessage),
				showToast: false
			})
			return e
		}).finally(() => {
			loading.value = false
		})
	}

	return {
		getProspectAddressData,
		loading,
		billables,
		getProspectAddressWithCoordinates,
		getProspectAddressWithPlaceId
	}
}
